import React, { CSSProperties, ReactNode, useContext, useState } from 'react';
import Projects from '../Projects/Projects';
import Libraries from '../Libraries/Libraries';
import Worksets from '../Worksets/Worksets';
import { ClientContext } from '../Clients.Provider';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import { HealthCheck, Tag, UserRoleEnum, useToggleClientPrivateMutation } from '../../../generated/graphql';
import { useCurrentUser } from '../../../App.hooks';
import EssentialsNew from '../../../components/main/EssentialsNew/EssentialsNew';
import DeleteClientTagDialog from '../../../components/dialogs/DeleteClientTagDialog/DeleteClientTagDialog';
import EditClientDialog from '../../../components/dialogs/EditClientDialog/EditClientDialog';
import DeleteClientHealthChecksDialog from '../../../components/dialogs/DeleteClientHealthChecksDialog/DeleteClientHealthChecksDialog';
import { Eye, EyeOff, FolderKanban, Library, Pencil, Package } from 'lucide-react';

type OverviewProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const Overview = (props: OverviewProps) => {
    const [activeTab, setActiveTab] = useState(0);
    const [editClientVisible, setEditClientVisible] = useState(false);
    const [deleteClientTagVisible, setDeleteClientTagVisible] = useState(false);
    const [deleteClientHealthCheckVisible, setDeleteClientHealthCheckVisible] = useState(false);
    const [selectedTagId, setselectedTagId] = useState("");
    const [selectedHealthCheckId, setselectedHealthCheckId] = useState("");
    const client = useContext(ClientContext);
    const [togglePrivate, togglePrivateResult] = useToggleClientPrivateMutation();
    const currentUser = useCurrentUser();
    const isGlobalAdmin = currentUser?.role === UserRoleEnum.Admin;

    const togglePrivateHandle = async () => {
        await togglePrivate({
            variables: {
                id: client?.data?.Client?.id
            }
        });
        client?.refetch();
    };

    const handleOnClose = () => {
        setDeleteClientHealthCheckVisible(false);
        setDeleteClientTagVisible(false);
        setEditClientVisible(false);
        client?.refetch();
    };

    const handleOnRemoveTag = (tagId: string) => {
        setselectedTagId(tagId);
        setDeleteClientTagVisible(true);
    };

    const handleOnHealthCheckTag = (tagId: string) => {
        setselectedHealthCheckId(tagId);
        setDeleteClientHealthCheckVisible(true);
    };

    const tabs = [
        { label: 'Libraries', icon: Library },
        ...(isGlobalAdmin ? [{ label: 'Worksets', icon: Package }] : []),
        { label: 'Projects', icon: FolderKanban },
    ];

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Action toolbar */}
            <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={(client && client.data?.Client?.isAdmin) || false}>
                <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <button className="btn btn-ghost text-sm" disabled={togglePrivateResult.loading} onClick={togglePrivateHandle}>
                        {client?.data?.Client?.isPrivate === true ? <><EyeOff size={15} /> Private</> : <><Eye size={15} /> Public</>}
                    </button>
                    <button className="btn btn-ghost text-sm" onClick={() => setEditClientVisible(true)}>
                        <Pencil size={15} /> Edit
                    </button>
                </div>
            </ShowOnRole>

            {/* Essentials panel */}
            <EssentialsNew
                persistenceKey="client-overview"
                className="mx-4 mt-3 flex-shrink-0"
                isAdmin={(client && client.data?.Client?.isAdmin) || false}
                onRemoveTag={handleOnRemoveTag}
                onRemoveHealthCheck={handleOnHealthCheckTag}
                tags={client?.data?.Client?.tags?.map<Tag>(t => t as Tag)}
                healthChecks={client?.data?.Client?.healthChecks?.map<HealthCheck>(c => c as HealthCheck)}
                rows={[
                    { title: "Name", value: client?.data?.Client?.name || "" },
                    { title: "Long Name", value: client?.data?.Client?.longName || "" },
                    { title: "Description", value: client?.data?.Client?.description || "" },
                    { title: "Default Library", value: client?.data?.Client?.defaultLibrary?.name || "", link: `/clients/${client?.data?.Client?.id}/library/${client?.data?.Client?.defaultLibrary?.id}` }
                ]}
            />

            {/* Inner tab bar */}
            <div className="flex-shrink-0 px-4 pt-3 pb-1">
                <div className="tab-bar" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                    {tabs.map((tab, i) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.label}
                                onClick={() => setActiveTab(i)}
                                className={activeTab === i ? 'tab-item-active' : 'tab-item'}
                            >
                                <Icon size={14} strokeWidth={1.5} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 min-h-0 relative">
                <div className="absolute inset-0 flex flex-col overflow-hidden">
                    {tabs[activeTab]?.label === 'Libraries' && <Libraries />}
                    {tabs[activeTab]?.label === 'Worksets' && <Worksets clientId={client?.data?.Client?.id || ""} isLoading={false} worksets={client?.data?.Client?.worksets || []} refresh={() => client?.refetch()} />}
                    {tabs[activeTab]?.label === 'Projects' && <Projects />}
                </div>
            </div>

            {deleteClientHealthCheckVisible && <DeleteClientHealthChecksDialog id={client?.data?.Client?.id || ""} healthCheckId={selectedHealthCheckId} visible={deleteClientHealthCheckVisible} onClose={handleOnClose} />}
            {deleteClientTagVisible && <DeleteClientTagDialog id={client?.data?.Client?.id || ""} tagId={selectedTagId} visible={deleteClientTagVisible} onClose={handleOnClose} />}
            {editClientVisible && <EditClientDialog id={client?.data?.Client?.id || ""} visible={editClientVisible} onClose={handleOnClose} />}
        </div>
    );
};

export default Overview;
